import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import AdminBackButton from "@/components/AdminBackButton";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Save, Shield, ClipboardCheck, Plus, Trash2, Camera } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { PhotoUploader } from "@/components/PhotoUploader";

export default function SafetyQualityPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Safety state
  const [safety, setSafety] = useState({
    toolbox_topic: "",
    near_miss: false,
    near_miss_desc: "",
    incident: false,
    incident_desc: "",
    inspections_completed: false,
    env_issue: false,
    env_issue_desc: "",
  });

  // Quality notes
  const [qualityNotes, setQualityNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [faceCond, setFaceCond] = useState("");
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, safetyData, notes, photoData] = await Promise.all([
          api.get(`/reports/${reportId}`),
          api.get(`/reports/${reportId}/safety`),
          api.get(`/reports/${reportId}/quality-notes`),
          api.get(`/reports/${reportId}/photos`),
        ]);
        setReport(r);
        if (safetyData && Object.keys(safetyData).length > 0) {
          setSafety({
            toolbox_topic: safetyData.toolbox_topic || "",
            near_miss: safetyData.near_miss || false,
            near_miss_desc: safetyData.near_miss_desc || "",
            incident: safetyData.incident || false,
            incident_desc: safetyData.incident_desc || "",
            inspections_completed: safetyData.inspections_completed || false,
            env_issue: safetyData.env_issue || false,
            env_issue_desc: safetyData.env_issue_desc || "",
          });
        }
        setQualityNotes(notes || []);
        setPhotos(photoData || []);
      } catch (err) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reportId]);

  const isReadOnly = report?.status === "Submitted";
  const sectionPhotosCount = (s) => photos.filter((p) => p.section === s).length;

  const handleSaveSafety = async () => {
    setSaving(true);
    try {
      await api.put(`/reports/${reportId}/safety`, safety);
      toast.success("Safety record saved");
    } catch (err) {
      toast.error("Failed to save safety data");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) { toast.error("Note text required"); return; }
    try {
      await api.post(`/reports/${reportId}/quality-notes`, { note: newNote, face_condition: faceCond });
      const notes = await api.get(`/reports/${reportId}/quality-notes`);
      setQualityNotes(notes || []);
      setNewNote("");
      setFaceCond("");
      toast.success("Quality note added");
    } catch (err) {
      toast.error("Failed to add note");
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await api.delete(`/reports/${reportId}/quality-notes/${id}`);
      setQualityNotes(qualityNotes.filter(n => n.id !== id));
      toast.success("Note deleted");
    } catch (err) {
      toast.error("Failed to delete note");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen" data-testid="safety-quality-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <AdminBackButton />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        {/* Safety Section */}
        <Card className="border-border bg-card mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="font-headline text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-400" strokeWidth={1.5} /> Safety & Environment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Toolbox Talk Topic</Label>
              <Input data-testid="toolbox-topic" value={safety.toolbox_topic} onChange={(e) => setSafety({ ...safety, toolbox_topic: e.target.value })} placeholder="Today's toolbox topic" className="bg-background border-border" disabled={isReadOnly} />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <Label className="text-sm">Near Miss?</Label>
                <Switch data-testid="near-miss-toggle" checked={safety.near_miss} onCheckedChange={(v) => setSafety({ ...safety, near_miss: v })} disabled={isReadOnly} />
              </div>
              {safety.near_miss && (
                <Input data-testid="near-miss-desc" value={safety.near_miss_desc} onChange={(e) => setSafety({ ...safety, near_miss_desc: e.target.value })} placeholder="Describe near miss..." className="bg-background border-border" disabled={isReadOnly} />
              )}

              <div className="flex items-center justify-between py-2">
                <Label className="text-sm">Incident?</Label>
                <Switch data-testid="incident-toggle" checked={safety.incident} onCheckedChange={(v) => setSafety({ ...safety, incident: v })} disabled={isReadOnly} />
              </div>
              {safety.incident && (
                <Input data-testid="incident-desc" value={safety.incident_desc} onChange={(e) => setSafety({ ...safety, incident_desc: e.target.value })} placeholder="Describe incident..." className="bg-background border-border" disabled={isReadOnly} />
              )}

              <div className="flex items-center justify-between py-2">
                <Label className="text-sm">Inspections Completed?</Label>
                <Switch data-testid="inspections-toggle" checked={safety.inspections_completed} onCheckedChange={(v) => setSafety({ ...safety, inspections_completed: v })} disabled={isReadOnly} />
              </div>

              <div className="flex items-center justify-between py-2">
                <Label className="text-sm">Environmental Issue?</Label>
                <Switch data-testid="env-issue-toggle" checked={safety.env_issue} onCheckedChange={(v) => setSafety({ ...safety, env_issue: v })} disabled={isReadOnly} />
              </div>
              {safety.env_issue && (
                <Input data-testid="env-issue-desc" value={safety.env_issue_desc} onChange={(e) => setSafety({ ...safety, env_issue_desc: e.target.value })} placeholder="Describe environmental issue..." className="bg-background border-border" disabled={isReadOnly} />
              )}
            </div>

            {!isReadOnly && (
              <Button onClick={handleSaveSafety} disabled={saving} data-testid="save-safety-button" className="w-full font-headline font-semibold">
                <Save className="h-4 w-4 mr-2" strokeWidth={2} />
                {saving ? "Saving..." : "Save Safety Record"}
              </Button>
            )}

            {/* Safety Photos */}
            <div className="pt-2 border-t border-border">
              <Label className="text-sm text-muted-foreground flex items-center gap-1.5 mb-3">
                <Camera className="h-3.5 w-3.5" strokeWidth={1.5} /> Site Photos
              </Label>
              <PhotoUploader
                reportId={reportId}
                section="safety"
                photos={photos}
                onPhotosChange={setPhotos}
                readOnly={isReadOnly}
              />
            </div>
          </CardContent>
        </Card>

        {/* Quality Notes Section */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-headline text-lg flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-purple-400" strokeWidth={1.5} /> Quality Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {qualityNotes.length > 0 && (
              <div className="space-y-2">
                {qualityNotes.map((n) => (
                  <div key={n.id} className="flex items-start justify-between p-3 rounded bg-background border border-border" data-testid={`quality-note-${n.id}`}>
                    <div>
                      <p className="text-sm">{n.note}</p>
                      {n.face_condition && <p className="text-xs text-muted-foreground mt-1">Face: {n.face_condition}</p>}
                    </div>
                    {!isReadOnly && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteNote(n.id)} className="shrink-0 ml-2">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!isReadOnly && (
              <div className="space-y-3 pt-2">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Face Condition</Label>
                  <Select value={faceCond} onValueChange={setFaceCond}>
                    <SelectTrigger data-testid="face-condition-select" className="bg-background border-border"><SelectValue placeholder="Select condition" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="Soft">Soft</SelectItem>
                      <SelectItem value="Hard">Hard</SelectItem>
                      <SelectItem value="Moist">Moist</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Quality Note</Label>
                  <textarea
                    data-testid="quality-note-input"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add quality observation..."
                    className="flex min-h-[60px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                  />
                </div>
                <Button onClick={handleAddNote} variant="outline" data-testid="add-quality-note-button" className="border-border">
                  <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add Note
                </Button>
              </div>
            )}

            {/* Quality Photos */}
            <div className="pt-2 border-t border-border">
              <Label className="text-sm text-muted-foreground flex items-center gap-1.5 mb-3">
                <Camera className="h-3.5 w-3.5" strokeWidth={1.5} /> Quality Photos
              </Label>
              <PhotoUploader
                reportId={reportId}
                section="quality"
                photos={photos}
                onPhotosChange={setPhotos}
                readOnly={isReadOnly}
              />
            </div>

            {qualityNotes.length === 0 && sectionPhotosCount("quality") === 0 && isReadOnly && (
              <p className="text-muted-foreground text-sm text-center py-4">No quality notes recorded</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
